import type { Occupancy as BookingOccupancy } from '@elara/domain/models/schemas/booking-schemas';
import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import type { RoomOptionModel } from '#models/RoomOptionModel.js';
import type { RoomRateModel } from '#models/RoomRateModel.js';

import { occupancyKey } from '@elara/domain/models/schemas/room-schemas';

export { occupancyKey } from '@elara/domain/models/schemas/room-schemas';
// Shortlist/itinerary identity matching now lives in @elara/domain (web + native share it).
export { metadataMatches } from '@elara/domain/item-identity';

/**
 * Resolves a rate from the room option for a given metadata blob. Identity is
 * `(productCode, occupancy)`. Falls back to legacy `selectedRateKey` for items
 * stored before the identity migration.
 */
export function resolveRateFromMetadata(
  roomOption: RoomOptionModel | undefined,
  metadata: { productCode?: string; occupancy?: Occupancy; selectedRateKey?: string } | null | undefined,
): RoomRateModel | undefined {
  if (!roomOption) return undefined;
  if (metadata?.productCode) {
    return roomOption.getRateByIdentity({ productCode: metadata.productCode, occupancy: metadata.occupancy });
  }
  if (metadata?.selectedRateKey) {
    return roomOption.getRateByRateKey(metadata.selectedRateKey);
  }
  return undefined;
}

/** Same identity rules as `resolveRateFromMetadata`, but returns availability. */
export function isRateAvailableForMetadata(
  roomOption: RoomOptionModel | undefined,
  metadata: { productCode?: string; occupancy?: Occupancy; selectedRateKey?: string } | null | undefined,
): boolean {
  if (!roomOption) return false;
  if (metadata?.productCode) {
    return roomOption.isRateAvailableByIdentity({
      productCode: metadata.productCode,
      occupancy: metadata.occupancy,
    });
  }
  if (metadata?.selectedRateKey) {
    return roomOption.isRateAvailable(metadata.selectedRateKey);
  }
  return roomOption.isAvailable;
}

/**
 * Convert the booking-room occupancy shape (`{ adults, children: [{ age }] }`,
 * `booking-schemas`) to the rate-search shape (`{ adults, children?, childrenAges? }`,
 * `room-schemas`) — the form `getRateByIdentity` and `occupancyKey` expect.
 */
export function bookingOccupancyToRateOccupancy(occupancy: BookingOccupancy): Occupancy {
  if (occupancy.children.length === 0) return { adults: occupancy.adults };
  return {
    adults: occupancy.adults,
    children: occupancy.children.length,
    childrenAges: occupancy.children.map((c) => c.age),
  };
}

/**
 * Formats an occupancy as a short user-facing string, e.g.:
 *  - { adults: 2 } → "2 adults"
 *  - { adults: 1 } → "1 adult"
 *  - { adults: 2, children: 1 } → "2 adults, 1 child"
 *  - { adults: 2, children: 2, childrenAges: [4, 7] } → "2 adults, 2 children (ages 4, 7)"
 */
export function formatOccupancy(occupancy: Occupancy): string {
  const adultPart = `${occupancy.adults} ${occupancy.adults === 1 ? 'adult' : 'adults'}`;
  const children = occupancy.children ?? 0;
  if (children === 0) return adultPart;
  let childPart = `${children} ${children === 1 ? 'child' : 'children'}`;
  const ages = occupancy.childrenAges?.filter((a) => Number.isFinite(a));
  if (ages && ages.length > 0) {
    childPart += ` (${ages.length === 1 ? 'age' : 'ages'} ${ages.join(', ')})`;
  }
  return `${adultPart}, ${childPart}`;
}

/** Tiny chip-friendly variant: "2A" / "2A · 1C" */
export function formatOccupancyChip(occupancy: Occupancy): string {
  const parts = [`${occupancy.adults}A`];
  const children = occupancy.children ?? 0;
  if (children > 0) parts.push(`${children}C`);
  return parts.join(' · ');
}

/**
 * Picks the most-recently-fetched occupancy among rates whose `fetchedAt` is set,
 * falling back to the first rate's occupancy. Returns undefined if no rate has occupancy.
 */
/** Coerces a Firestore Timestamp / Date / ISO string to epoch ms. Returns 0 for anything unrecognized. */
function timestampToMs(v: unknown): number {
  if (!v) return 0;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'string') return Date.parse(v) || 0;
  if (typeof v === 'object' && 'toDate' in v && typeof (v as { toDate?: unknown }).toDate === 'function') {
    try {
      return (v as { toDate: () => Date }).toDate().getTime();
    } catch {
      return 0;
    }
  }
  return 0;
}

export function pickDefaultOccupancyFromRates(
  rates: Array<{
    occupancy?: Occupancy;
    available?: boolean;
    fetchedAt?: Date | string | { toDate?: () => Date } | undefined;
  }>,
  /**
   * The party the search was requested for (`roomOption.search.occupancy`), when the room records it.
   * It wins whenever the room still has a bookable rate for it: a room can carry several parties, and
   * the most-recently-fetched rule below cannot tell the requested party from a fallback one priced in
   * the same breath. A Travelport family search always also prices the adults alone, and the two land
   * milliseconds apart, so without this the opening tab is a coin toss.
   */
  requested?: Occupancy,
): Occupancy | undefined {
  const ratesWithOcc = rates.filter((r): r is typeof r & { occupancy: Occupancy } => !!r.occupancy);
  if (ratesWithOcc.length === 0) return undefined;
  const live = ratesWithOcc.filter((r) => r.available !== false);
  const forRequested = (pool: typeof ratesWithOcc) =>
    requested ? pool.find((r) => occupancyKey(r.occupancy) === occupancyKey(requested)) : undefined;
  // The requested party, while it has something left to book. When it has nothing, open on a party
  // that does — opening on a page of "Sold out" while another party's rates are bookable left the
  // traveler at a dead end with the way out hidden in a dropdown (Andaz 5th Avenue, 2026-10-04).
  const requestedLive = forRequested(live);
  if (requestedLive) return requestedLive.occupancy;
  if (live.length > 0) {
    return live.toSorted((a, b) => timestampToMs(b.fetchedAt) - timestampToMs(a.fetchedAt))[0]?.occupancy;
  }
  // Nothing bookable for anyone: the requested party, sold out, is the truthful page to open on.
  const requestedAny = forRequested(ratesWithOcc);
  if (requestedAny) return requestedAny.occupancy;
  const sorted = ratesWithOcc.toSorted((a, b) => timestampToMs(b.fetchedAt) - timestampToMs(a.fetchedAt));
  return sorted[0]?.occupancy;
}

/** Returns the distinct occupancies in a rates array, ordered by most-recently-fetched first. */
export function distinctOccupancies(
  rates: Array<{ occupancy?: Occupancy; fetchedAt?: Date | string | { toDate?: () => Date } | undefined }>,
): Occupancy[] {
  const seen = new Map<string, Occupancy>();
  // Sort by fetchedAt desc so earliest seen wins (and represents the latest)
  const sorted = rates.toSorted((a, b) => timestampToMs(b.fetchedAt) - timestampToMs(a.fetchedAt));
  for (const r of sorted) {
    if (!r.occupancy) continue;
    const k = occupancyKey(r.occupancy);
    if (!seen.has(k)) seen.set(k, r.occupancy);
  }
  return [...seen.values()];
}
